export type PwaUpdateHandler = (worker: ServiceWorker) => void;

export const registerPwa = (onUpdate: PwaUpdateHandler): (() => void) => {
  if (!('serviceWorker' in navigator)) {
    return () => undefined;
  }

  let disposed = false;
  let refreshing = false;
  const hadController = navigator.serviceWorker.controller !== null;
  const handleControllerChange = (): void => {
    if (hadController && !refreshing) {
      refreshing = true;
      location.reload();
    }
  };
  navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

  const handleWorker = (worker: ServiceWorker | null): void => {
    if (!worker) {
      return;
    }
    const handleStateChange = (): void => {
      if (!disposed && worker.state === 'installed' && navigator.serviceWorker.controller) {
        onUpdate(worker);
      }
    };
    worker.addEventListener('statechange', handleStateChange);
    handleStateChange();
  };

  const register = async (): Promise<void> => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none',
      });
      if (disposed) {
        return;
      }
      if (registration.waiting && navigator.serviceWorker.controller) {
        onUpdate(registration.waiting);
      }
      registration.addEventListener('updatefound', () => handleWorker(registration.installing));
    } catch {
      // PWA registration must never prevent the web application from starting.
    }
  };

  if (document.readyState === 'complete') {
    void register();
  } else {
    addEventListener('load', register, { once: true });
  }

  return () => {
    disposed = true;
    removeEventListener('load', register);
    navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
  };
};
